import { Link } from 'react-router-dom';
import { trackPhoneClick, trackEmailClick, trackSocialClick } from '../../hooks/useAnalytics';

const socialStyles = `
  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    14% { transform: scale(1.15); }
    28% { transform: scale(1); }
    42% { transform: scale(1.1); }
    56% { transform: scale(1); }
  }
  .social-heartbeat {
    animation: heartbeat 2.4s ease-in-out infinite;
  }
  .social-heartbeat-delay1 {
    animation: heartbeat 2.4s ease-in-out infinite;
    animation-delay: 0.4s;
  }
  .social-heartbeat-delay2 {
    animation: heartbeat 2.4s ease-in-out infinite;
    animation-delay: 0.8s;
  }
`;

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 text-white">
      <style>{socialStyles}</style>
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6 flex flex-col items-center text-center">
            <img 
              src="https://static.readdy.ai/image/afa77d5851d304160205a8c0c44e2b32/c8356ec8795d7c13ad73f0009fda5fbf.png" 
              alt="Fiber Güvenlik Logo" 
              className="h-24 w-auto object-contain"
            />
            <p className="text-gray-300 text-sm leading-relaxed">
              Fiber optik altyapı ve güvenlik sistemlerinde profesyonel çözümler sunuyoruz. 7/24 acil müdahale hizmetimizle yanınızdayız.
            </p>
            <div className="flex gap-5 justify-center">
              <a
                href="https://www.youtube.com/channel/UCre9bsVpBqkHVtniQuVfYdA"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('youtube', 'footer')}
                className="social-heartbeat w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/50 rounded-full transition-all duration-300 hover:scale-125 cursor-pointer"
                aria-label="YouTube"
              >
                <i className="ri-youtube-fill text-xl"></i>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61587743353286"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('facebook', 'footer')}
                className="social-heartbeat-delay1 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-sky-600 hover:shadow-xl hover:shadow-sky-500/50 rounded-full transition-all duration-300 hover:scale-125 cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a
                href="https://www.instagram.com/fiber_guvenlik/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('instagram', 'footer')}
                className="social-heartbeat-delay2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-400 hover:shadow-xl hover:shadow-pink-500/50 rounded-full transition-all duration-300 hover:scale-125 cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-fill text-xl"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Hızlı Linkler</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Ana Sayfa
                </Link>
              </li>
              <li>
                <Link to="/fiber-hizmetleri" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Fiber Optik Hizmetleri
                </Link>
              </li>
              <li>
                <Link to="/guvenlik-sistemleri" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Güvenlik Sistemleri
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/iletisim" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  İletişim
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4">Hizmetlerimiz</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/fiber-hizmetleri/fiber-kablo-tamiri" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Fiber Kablo Tamiri
                </Link>
              </li>
              <li>
                <Link to="/fiber-hizmetleri/fiber-sonlandirma" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Fiber Sonlandırma
                </Link>
              </li>
              <li>
                <Link to="/guvenlik-sistemleri/ip-kamera" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  IP Kamera Sistemleri
                </Link>
              </li>
              <li>
                <Link to="/guvenlik-sistemleri/hirsiz-alarm" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap">
                  Hırsız Alarm Sistemleri
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">İletişim</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <i className="ri-map-pin-line text-cyan-400 mt-1"></i>
                <span className="text-slate-400 text-sm">
                  Güneşli Mah. 1272. Sk. No: 1<br />
                  İç Kapı No: 7<br />
                  Bağcılar/İstanbul
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-phone-line text-cyan-400"></i>
                <a
                  href="tel:+905537165561"
                  onClick={() => trackPhoneClick('+905537165561', 'footer')}
                  className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap"
                >
                  +90 553 716 55 61
                </a>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-mail-line text-cyan-400"></i>
                <a
                  href="mailto:info@fiberek.com"
                  onClick={() => trackEmailClick('info@fiberek.com', 'footer')}
                  className="text-slate-400 hover:text-cyan-400 transition-colors text-sm whitespace-nowrap"
                >
                  info@fiberek.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-8 pt-8 text-center">
          <p className="text-slate-400 text-sm">
            © 2024 Fiber ve Güvenlik Çözümleri. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  );
}